import { PlusOutlined } from '@ant-design/icons';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Form } from 'antd';
import { useState } from 'react';

import { SList } from 'app/components/StaycationUI/List';
import type { ExperienceType } from 'app/components/commons/Package/General/Types';

import { ExperienceItem } from './ExperienceItem/ExperienceItem';
import './ExperienceList.scss';
import { ExperienceModal } from './ExperienceModal/ExperienceModal';

export const ExperienceList = ({
  mode,
  selectedExperiences,
  hotelExperiences,
  staycationExperiences,
  move,
}: Props) => {
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);

  const isEmpty = !selectedExperiences || !selectedExperiences.length;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );

  return (
    <div className="list-experiences">
      <Form.List name="experiences">
        {(fields, { add, remove }) => {
          const onSelect = (values: ExperienceType[]) =>
            values
              .filter(
                (experience) =>
                  !selectedExperiences?.some((e) => e.id === experience.id)
              )
              .forEach((experience) => add({ ...experience, included: null }));

          const items = mode === 'edit' || !isEmpty ? fields : [];
          const itemIds = items.map(
            (field, index) => selectedExperiences?.[index]?.id ?? field.key
          );

          const handleDragEnd = ({ active, over }: DragEndEvent) => {
            if (!over || active.id === over.id) {
              return;
            }

            const oldIndex = itemIds.findIndex((id) => id === active.id);
            const newIndex = itemIds.findIndex((id) => id === over.id);

            if (oldIndex === -1 || newIndex === -1) {
              return;
            }

            move({ oldIndex, newIndex });
          };

          return (
            <>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={itemIds}
                  strategy={verticalListSortingStrategy}
                >
                  <SList
                    data={items}
                    renderItem={(field, index) => (
                      <ExperienceItem
                        field={field}
                        dndId={itemIds[index]}
                        experience={selectedExperiences?.[index]}
                        mode={mode}
                        remove={() => remove(index)}
                        key={itemIds[index]}
                        disabled={mode === 'view'}
                      />
                    )}
                    bordered
                  />
                </SortableContext>
              </DndContext>
              {mode === 'edit' && (
                <div
                  onClick={() => setIsSelectorOpen(true)}
                  className="list-experiences__add-button"
                >
                  <PlusOutlined />
                  <span className="text">{`Add experiences`}</span>
                </div>
              )}
              <ExperienceModal
                visible={isSelectorOpen}
                onSelect={onSelect}
                onCancel={() => setIsSelectorOpen(false)}
                hotelExperiences={hotelExperiences}
                staycationExperiences={staycationExperiences}
              />
            </>
          );
        }}
      </Form.List>
    </div>
  );
};

export const SortableExperiencesList = (props: SortableProps) => (
  <ExperienceList {...props} />
);

type Props = {
  mode: string;
  selectedExperiences?: ExperienceType[];
  hotelExperiences?: ExperienceType[];
  staycationExperiences?: ExperienceType[];
  move: (indexes: { oldIndex: number; newIndex: number }) => void;
};

type SortableProps = Props;
